import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { parseDate, queryKeys } from "./api";
import { useDebounced } from "./use-debounced";

/** Attente apres la derniere frappe avant de demander au serveur ce qu'il comprend. */
export const PREVIEW_DELAY_MS = 400;

/** « si pas de redoublement » devient « Si pas de redoublement. » */
const sentence = (text: string): string =>
  `${text.charAt(0).toLocaleUpperCase("fr-FR")}${text.slice(1)}${text.endsWith(".") ? "" : "."}`;

/**
 * L'apercu d'une date floue (planche « Point ») : ce que le serveur comprend du texte tape,
 * demande une fois la frappe arretee (`POST /api/t/dates/parse`, toujours en 200). Une reussite
 * dit « Compris : ... » et la reserve eventuelle ; un echec dit le message du lecteur, avec le
 * chemin des reglages quand il manque la date de naissance.
 */
export function DatePreview({ raw, id }: { raw: string; id: string }) {
  const text = useDebounced(raw.trim(), PREVIEW_DELAY_MS);
  const preview = useQuery({
    queryKey: queryKeys.parse(text),
    queryFn: () => parseDate(text),
    enabled: text !== "",
    staleTime: 60_000,
  });

  let content: ReactNode = null;
  if (text !== "" && preview.data?.ok === true) {
    content = (
      <div className="flex items-start gap-2.5 rounded-[10px] bg-[rgba(111,211,160,0.12)] px-3 py-2.5 text-sm text-tl-ok">
        <svg
          aria-hidden="true"
          className="mt-px shrink-0"
          fill="none"
          height="18"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.2"
          viewBox="0 0 24 24"
          width="18"
        >
          <path d="M5 12l5 5 9-10" />
        </svg>
        <span>
          <strong>Compris : {preview.data.label}.</strong>
          {preview.data.caveat ? (
            <>
              <br />
              {sentence(preview.data.caveat)} Tu pourras corriger.
            </>
          ) : null}
        </span>
      </div>
    );
  } else if (text !== "" && preview.data?.ok === false && preview.data.reason !== "empty") {
    content = (
      <div className="flex flex-col items-start rounded-[10px] bg-[rgba(255,138,128,0.10)] px-3 py-2.5 text-sm text-destructive">
        <span>{preview.data.message}</span>
        {preview.data.reason === "needs_birth_date" ? (
          <Link
            className="mt-1 inline-flex min-h-11 items-center text-tl-accent underline"
            to="/reglages"
          >
            Renseigner ma date de naissance
          </Link>
        ) : null}
      </div>
    );
  } else if (text !== "" && preview.isError) {
    content = (
      <p className="m-0 text-sm text-muted-foreground">
        L'aperçu ne répond pas. Tu peux quand même enregistrer.
      </p>
    );
  }

  return (
    <div aria-live="polite" id={id}>
      {content}
    </div>
  );
}
